import React, { useState, useEffect } from 'react';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import GroundSelector from './GroundSelector';
import CaseList from './CaseList';
import CaseDetail from './CaseDetail';
import { tenantService, evictionService, complianceService, arrearsService } from '../../services/api';
import { useNavigate } from 'react-router-dom';

export default function EvictionWorkflow() {
    const navigate = useNavigate();
    const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
    const [selectedCase, setSelectedCase] = useState<any>(null);

    // Wizard State
    const [step, setStep] = useState(1);
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [tenants, setTenants] = useState<any[]>([]);
    const [selectedGround, setSelectedGround] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [properties, setProperties] = useState<any[]>([]);
    // Per-tenant arrears for the Step 1 list — pulled from the arrears
    // service so the list can be sorted "biggest debt first" and each row
    // can show an arrears badge with the amount owed.
    const [tenantArrearsMap, setTenantArrearsMap] = useState<Record<string, number>>({});
    // Outstanding amount at Stage 3 — pooled from the arrears engine for
    // the selected tenant, but always editable so the officer can
    // override (e.g. include unpaid utilities, partial settlements).
    const [outstandingAmount, setOutstandingAmount] = useState<string>('');
    const [outstandingLoaded, setOutstandingLoaded] = useState<boolean>(false);
    const [outstandingBreakdown, setOutstandingBreakdown] = useState<{
        arrears: number;
        rentAmount: number;
        missedMonths: number;
    } | null>(null);
    const [outstandingError, setOutstandingError] = useState<string | null>(null);

    useEffect(() => {
        if (view === 'create') {
            loadTenants();
        }
    }, [view]);

    const loadTenants = async () => {
        setLoading(true);
        try {
            const [tenRes, propRes] = await Promise.all([
                tenantService.getTenants(),
                complianceService.getProperties()
            ]);

            const tenData = tenRes?.data?.data || tenRes?.data;
            setTenants(Array.isArray(tenData) ? tenData : []);

            const propData = propRes?.data?.data || propRes?.data;
            setProperties(Array.isArray(propData) ? propData : []);

            // Bulk arrears — non-blocking. Builds tenantId → arrears map so
            // Step 1 can sort biggest-debt-first and show the amount inline.
            arrearsService.getTenants()
                .then((arrRes: any) => {
                    const rows: any[] = arrRes?.data?.data || arrRes?.data || [];
                    const map: Record<string, number> = {};
                    rows.forEach(r => {
                        const amt = Number(r?.arrears ?? r?.summary?.arrears ?? 0);
                        if (r?.tenantId) map[r.tenantId] = amt;
                    });
                    setTenantArrearsMap(map);
                })
                .catch((err: any) => console.warn('[Eviction] arrears bulk fetch failed:', err?.message));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Resolve a tenant's arrears from the engine map first, then the
    // tenant record's own `arrears` field (legacy), then 0.
    const arrearsFor = (tenant: any) => {
        const id = tenant?.id;
        if (id && tenantArrearsMap[id] != null) return tenantArrearsMap[id];
        return Number(tenant?.arrears ?? 0);
    };

    const getPropertyAddress = (propertyId: string) => {
        if (!propertyId || !properties.length) return 'No Address';
        
        const prop = properties.find(p => String(p.id) === String(propertyId));
        if (!prop) return 'Unknown Property';
        
        const addr = prop.address as any;
        if (!addr) return 'No Address';
        
        // Handle address object safely (null check for typeof object)
        if (typeof addr === 'object' && addr !== null) {
            return `${addr.line1 || ''} ${addr.city || ''}`.trim() || 'No Address';
        }
        
        // Handle plain strings or numbers
        return String(addr || 'Unknown Address');
    };

    // Pool the outstanding amount from the arrears engine the moment the
    // officer reaches Stage 3. The field is then editable so they can
    // override (e.g. include unpaid utilities, accept a partial settlement,
    // or zero it out for a non-rent ground like Ground 12 nuisance).
    useEffect(() => {
        if (step !== 3 || !selectedTenantId) return;
        let cancelled = false;
        setOutstandingLoaded(false);
        setOutstandingError(null);

        arrearsService.getTenantDetail(selectedTenantId)
            .then((res: any) => {
                if (cancelled) return;
                const data: any = res.data?.data || {};
                // Try three sources in order:
                //   1. arrears engine (rent due − rent paid)
                //   2. arrears field on the tenant record itself (legacy)
                //   3. zero (officer enters manually)
                const engineArrears = Number(data.summary?.arrears ?? data.arrears ?? 0);
                const tenantRecord = tenants.find(t => t.id === selectedTenantId);
                const legacyArrears = Number(tenantRecord?.arrears ?? 0);
                const arrears = engineArrears > 0 ? engineArrears
                              : legacyArrears > 0 ? legacyArrears
                              : 0;

                setOutstandingBreakdown({
                    arrears,
                    rentAmount: Number(data.rentAmount ?? tenantRecord?.rentAmount ?? 0),
                    missedMonths: Number(data.summary?.missedMonths ?? 0),
                });
                // Surface engine messages (e.g. "Missing tenancy start date or
                // rent amount") so the officer knows WHY the field is blank
                // and can correct the tenancy record or enter manually.
                if (arrears === 0 && data.message) {
                    setOutstandingError(`Engine could not compute arrears: ${data.message}.`);
                }
                setOutstandingAmount(arrears > 0 ? arrears.toFixed(2) : '');
            })
            .catch((err: any) => {
                if (cancelled) return;
                console.warn('[Eviction] arrears fetch failed:', err?.message);
                setOutstandingError('Could not load arrears automatically. Enter the amount manually.');
            })
            .finally(() => { if (!cancelled) setOutstandingLoaded(true); });

        return () => { cancelled = true; };
    }, [step, selectedTenantId, tenants]);

    const filteredTenants = tenants
        .filter(t => {
            if (!searchTerm) return true;
            const lowerSearch = searchTerm.toLowerCase();
            return (
                t.name?.toLowerCase().includes(lowerSearch) ||
                t.email?.toLowerCase().includes(lowerSearch) ||
                getPropertyAddress(t.propertyId).toLowerCase().includes(lowerSearch)
            );
        })
        // Sort biggest-debt-first so officers see the at-risk tenants
        // immediately. Ties (or no-debt tenants) fall back to alphabetical
        // by name so the rest of the list stays predictable.
        .sort((a, b) => {
            const arrA = arrearsFor(a);
            const arrB = arrearsFor(b);
            if (arrB !== arrA) return arrB - arrA;
            return String(a?.name || '').localeCompare(String(b?.name || ''));
        });

    const handleCreateCase = async () => {
        if (!selectedTenantId || !selectedGround) return;
        setSubmitting(true);
        try {
            const tenant = tenants.find(t => t.id === selectedTenantId);
            // Outstanding amount may have been edited by the officer.
            // Empty / non-numeric inputs are treated as 0 (non-rent grounds).
            const parsedOutstanding = parseFloat(outstandingAmount);
            const outstanding = Number.isFinite(parsedOutstanding) ? parsedOutstanding : 0;
            await evictionService.createCase({
                tenantId: selectedTenantId,
                propertyId: tenant.propertyId,
                groundCode: selectedGround.code,
                outstandingAmount: outstanding,
            });

            // Success! Go to detail view of new case or list
            alert('Eviction case created successfully! Notice generated.');
            setStep(1); // Reset wizard
            setSelectedTenantId('');
            setSelectedGround(null);
            setOutstandingAmount('');
            setOutstandingBreakdown(null);
            setOutstandingLoaded(false);
            setOutstandingError(null);

            // If API returns the new case, we could go straight to detail
            // For now, let's go to list to keep it simple and safe
            setView('list');

        } catch (e: any) {
            alert('Error: ' + (e.response?.data?.message || e.message));
        } finally {
            setSubmitting(false);
        }
    };

    const refreshSelectedCase = async () => {
        if (!selectedCase) return;
        try {
            // Use only the ID (GUID) for the API call to avoid '#' from PK/SK
            const res = await evictionService.getCase(selectedCase.id);
            // Ensure we extract the internal data item correctly
            const caseData = res.data?.data || res.data;
            if (caseData && typeof caseData === 'object' && !Array.isArray(caseData)) {
                // Merge old state with new data for stability
                setSelectedCase((prev: any) => ({ ...prev, ...caseData }));
            }
        } catch (e) {
            console.error("Failed to refresh case", e);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                {view === 'list' && (
                    <>
                        <div className="mb-8">
                            <h1 className="text-2xl font-bold text-gray-900">Eviction Cases</h1>
                            <p className="mt-1 text-sm text-gray-500">Manage possession proceedings under Renters' Rights Act 2025</p>
                        </div>
                        <CaseList
                            onSelectCase={(c) => {
                                setSelectedCase(c);
                                setView('detail');
                            }}
                            onNewCase={() => {
                                setView('create');
                                setStep(1);
                            }}
                        />
                    </>
                )}

                {view === 'detail' && selectedCase && (
                    <>
                        <div className="mb-8">
                            <h1 className="text-2xl font-bold text-gray-900">Case Management</h1>
                        </div>
                        <CaseDetail
                            caseData={selectedCase}
                            onBack={() => {
                                setSelectedCase(null);
                                setView('list');
                            }}
                            onUpdate={refreshSelectedCase}
                        />
                    </>
                )}

                {view === 'create' && (
                    <>
                        <div className="mb-8 flex justify-between items-center">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Start Eviction Process</h1>
                                <p className="mt-1 text-sm text-gray-500">Renters' Rights Act 2025 Compliant Workflow</p>
                            </div>
                            <button onClick={() => setView('list')} className="text-gray-500 hover:text-gray-700">Cancel</button>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-8 max-w-4xl mx-auto">
                            <div className="flex items-center justify-between text-sm font-medium text-gray-500">
                                <span className={step >= 1 ? 'text-indigo-600' : ''}>1. Select Tenant</span>
                                <span className={step >= 2 ? 'text-indigo-600' : ''}>2. Select Ground</span>
                                <span className={step >= 3 ? 'text-indigo-600' : ''}>3. Review & Create</span>
                            </div>
                            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-indigo-600 transition-all duration-500"
                                    style={{ width: `${(step / 3) * 100}%` }}
                                />
                            </div>
                        </div>

                        <div className="bg-white shadow sm:rounded-lg p-6 max-w-4xl mx-auto">
                            {step === 1 && (
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">Select Tenant</h2>
                                    <div className="space-y-4">
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <i className="fas fa-search text-gray-400"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                placeholder="Search by tenant name, email or property..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>

                                        {loading && (
                                            <div className="flex justify-center py-8">
                                                <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
                                            </div>
                                        )}
                                        
                                        {!loading && filteredTenants.length === 0 && (
                                            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                                                <p className="text-gray-500">No matching tenants found.</p>
                                            </div>
                                        )}

                                        {!loading && filteredTenants.length > 0 && (
                                            <p className="text-xs text-gray-500 -mt-2 mb-2">
                                                <i className="fas fa-sort-amount-down mr-1 text-rose-500"></i>
                                                Sorted by arrears, highest first. Tenants currently in arrears are flagged red.
                                            </p>
                                        )}

                                        <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                            {filteredTenants.map(tenant => {
                                                const arrears = arrearsFor(tenant);
                                                const hasArrears = arrears > 0;
                                                return (
                                                <div
                                                    key={tenant?.id || Math.random()}
                                                    onClick={() => tenant?.id && setSelectedTenantId(tenant.id)}
                                                    className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex justify-between items-center ${
                                                        selectedTenantId === tenant?.id
                                                            ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                                                            : hasArrears
                                                                ? 'border-rose-200 bg-rose-50/40 shadow-sm'
                                                                : 'border-gray-200 shadow-sm'
                                                    }`}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-bold text-gray-900 truncate">{String(tenant?.name || 'Unnamed Tenant')}</span>
                                                            {hasArrears && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200 uppercase tracking-wide">
                                                                    <i className="fas fa-pound-sign mr-1"></i>
                                                                    Arrears £{arrears.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate">{String(tenant?.email || 'No email')}</div>
                                                        <div className="text-[10px] font-medium text-indigo-600 mt-1 uppercase tracking-wide">
                                                            <i className="fas fa-building mr-1"></i>
                                                            {String(getPropertyAddress(tenant?.propertyId))}
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-[10px] text-gray-400">Since: {tenant?.tenancyStartDate ? new Date(tenant.tenancyStartDate).toLocaleDateString() : 'N/A'}</div>
                                                        {selectedTenantId === tenant?.id && (
                                                            <div className="text-indigo-600 mt-1">
                                                                <i className="fas fa-check-circle"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>

                                        <button
                                            disabled={!selectedTenantId}
                                            onClick={() => setStep(2)}
                                            className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300"
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div>
                                    <GroundSelector
                                        onSelect={setSelectedGround}
                                        selectedGroundCode={selectedGround?.code}
                                        tenancyStartDate={tenants.find(t => t.id === selectedTenantId)?.tenancyStartDate}
                                    />

                                    <div className="mt-6 flex justify-between">
                                        <button
                                            onClick={() => setStep(1)}
                                            className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            Back
                                        </button>
                                        <button
                                            disabled={!selectedGround}
                                            onClick={() => setStep(3)} // Go to review/create
                                            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300"
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900 mb-4">Confirm & Generate Notice</h2>
                                    <div className="bg-yellow-50 p-4 rounded-md mb-6">
                                        <p className="text-sm text-yellow-800">
                                            By clicking "Generate Notice", a new eviction case will be opened and a <strong>Section 8 Notice</strong> will be drafted using the selected grounds.
                                        </p>
                                    </div>

                                    <div className="border-t border-gray-200 py-4">
                                        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Tenant</dt>
                                                <dd className="mt-1 text-sm text-gray-900">{String(tenants.find(t => t.id === selectedTenantId)?.name || 'N/A')}</dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Ground</dt>
                                                <dd className="mt-1 text-sm text-gray-900">{String(selectedGround?.name || 'N/A')}</dd>
                                            </div>
                                        </dl>
                                    </div>

                                    {/* Outstanding amount — pre-populated from the arrears
                                        engine for the selected tenant, but always editable.
                                        Officer remains in full control; the field is a live
                                        input not a read-only summary. */}
                                    <div className="border-t border-gray-200 py-4">
                                        <label className="block text-sm font-medium text-gray-500 mb-1">
                                            Outstanding Amount (£)
                                            {!outstandingLoaded && (
                                                <span className="ml-2 text-xs text-gray-400">
                                                    <i className="fas fa-spinner fa-spin mr-1"></i>pulling from arrears…
                                                </span>
                                            )}
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-sm">£</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={outstandingAmount}
                                                onChange={e => setOutstandingAmount(e.target.value)}
                                                disabled={!outstandingLoaded}
                                                placeholder="0.00"
                                                className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                            />
                                        </div>
                                        {outstandingBreakdown && outstandingBreakdown.arrears > 0 && (
                                            <p className="mt-2 text-xs text-gray-500">
                                                <i className="fas fa-info-circle mr-1 text-indigo-500"></i>
                                                Pre-filled from arrears engine: £{outstandingBreakdown.arrears.toFixed(2)} across {outstandingBreakdown.missedMonths} missed month{outstandingBreakdown.missedMonths === 1 ? '' : 's'} (rent £{outstandingBreakdown.rentAmount.toFixed(2)}/mo). Edit above to override.
                                            </p>
                                        )}
                                        {outstandingBreakdown && outstandingBreakdown.arrears === 0 && !outstandingError && (
                                            <p className="mt-2 text-xs text-gray-500">
                                                <i className="fas fa-info-circle mr-1 text-emerald-500"></i>
                                                Arrears engine returned <strong>£0.00</strong> for this tenant
                                                {outstandingBreakdown.rentAmount > 0 && <> (rent £{outstandingBreakdown.rentAmount.toFixed(2)}/mo)</>}.
                                                Enter the amount you're claiming if the ground requires it.
                                            </p>
                                        )}
                                        {outstandingError && (
                                            <p className="mt-2 text-xs text-amber-700">
                                                <i className="fas fa-exclamation-triangle mr-1"></i>{outstandingError}
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-6 flex justify-between">
                                        <button
                                            onClick={() => setStep(2)}
                                            className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            Back
                                        </button>
                                        <button
                                            disabled={submitting}
                                            onClick={handleCreateCase}
                                            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300"
                                        >
                                            {submitting ? 'Creating Case...' : 'Create Case & Generate Notice'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}
